import React, { useState, useEffect } from 'react';
import { Music, Play, Pause, Heart, Sparkles, Send, CheckCircle, Clock } from 'lucide-react';
import ApiClient from '../api/client';

const BirthdaySongMVP = () => {
  const [activeExample, setActiveExample] = useState(null);
  const [exampleSongs, setExampleSongs] = useState([]);
  const [loadingExamples, setLoadingExamples] = useState(true);
  const [formData, setFormData] = useState({
    customerEmail: '',
    recipientName: '',
    age: '',
    interests: '',
    specialMessage: '',
    songStyle: 'upbeat'
  });
  const [orderStatus, setOrderStatus] = useState(null);
  const [error, setError] = useState(null);

  // Load example songs on mount
  useEffect(() => {
    loadExamples();
  }, []);

  const loadExamples = async () => {
    try {
      const examples = await ApiClient.fetchExamples();
      setExampleSongs(examples);
    } catch (err) {
      console.error('Failed to load examples:', err);
      // Fallback to hardcoded examples if API fails
      setExampleSongs([
        {
          id: 1,
          title: "Sarah's Garden Party Song",
          recipient_name: "Sarah",
          age: 30,
          interests: "Gardening & Cooking",
          song_style: "Upbeat & Fun",
          lyrics: `🎵 Happy Birthday Sarah, thirty looks so fine,
With your garden blooming and your cooking so divine!
From morning dew to dinner time, you make each day so bright,
Sarah's turning thirty, let's celebrate tonight! 🎵

May your flowers always grow, may your recipes delight,
Another year of wonder, everything feels right!`,
          color: "from-pink-500 to-rose-500"
        },
        {
          id: 2,
          title: "Mike's Championship Song",
          recipient_name: "Mike",
          age: 45,
          interests: "Sports & Technology",
          song_style: "Energetic & Modern",
          lyrics: `🎵 Hey Mike it's your day, forty-five and going strong,
From the basketball court to the tech world all day long!
You code by day, you play by night, you're living life so right,
Mike's another year wiser, shining super bright! 🎵

Here's to touchdowns and breakthroughs, innovations yet to come,
Happy birthday champion, you're second to none!`,
          color: "from-blue-500 to-cyan-500"
        },
        {
          id: 3,
          title: "Emma's Magical Birthday",
          recipient_name: "Emma",
          age: 7,
          interests: "Unicorns & Drawing",
          song_style: "Playful & Magical",
          lyrics: `🎵 Emma's turning seven, it's a magical day,
With unicorns and rainbows, hip hip hooray!
Drawing all the colors, making art so bright,
Emma's birthday party, everything's just right! 🎵

Seven candles glowing, wishes in the air,
Emma you're amazing, sparkles everywhere!`,
          color: "from-purple-500 to-pink-500"
        }
      ]);
    } finally {
      setLoadingExamples(false);
    }
  };

  const togglePlay = (id) => {
    setActiveExample(activeExample === id ? null : id);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOrderStatus('submitting');
    setError(null);

    try {
      // Validate form
      if (!formData.customerEmail || !formData.recipientName || !formData.age || !formData.interests) {
        throw new Error('Please fill in all required fields');
      }

      // Create checkout session
      const { checkoutUrl } = await ApiClient.createCheckout(formData);

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;

    } catch (err) {
      console.error('Error submitting order:', err);
      setError(err.message || 'Failed to create order. Please try again.');
      setOrderStatus(null);
    }
  };

  const scrollToForm = () => {
    document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Music className="w-8 h-8 text-purple-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Little Happy Tunes
            </span>
          </div>
          <button
            onClick={scrollToForm}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all"
          >
            Create Your Song
          </button>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <div className="inline-block mb-4">
          <span className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold">
            AI-Powered Personalization
          </span>
        </div>
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
          Birthday Songs That Make Them Smile
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Create a one-of-a-kind personalized birthday song in minutes. Just tell us about them, and our AI will compose a unique musical gift they'll treasure forever.
        </p>
        <button
          onClick={scrollToForm}
          className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all transform hover:scale-105 inline-flex items-center gap-2"
        >
          <Sparkles className="w-5 h-5" />
          Get Started - $7.99
        </button>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-purple-600">1</span>
            </div>
            <h3 className="font-bold text-lg mb-2">Tell Us About Them</h3>
            <p className="text-gray-600">Share their name, age, interests, and what makes them special</p>
          </div>
          <div className="text-center">
            <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-pink-600">2</span>
            </div>
            <h3 className="font-bold text-lg mb-2">AI Creates Magic</h3>
            <p className="text-gray-600">Our AI composes personalized lyrics and generates the song</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-blue-600">3</span>
            </div>
            <h3 className="font-bold text-lg mb-2">Receive & Share</h3>
            <p className="text-gray-600">Get your unique song via email in 5-10 minutes</p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Listen to Examples</h2>
          <p className="text-gray-600 text-lg">Each song is unique and personalized - yours will be too!</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {exampleSongs.map((song) => (
            <div key={song.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className={`bg-gradient-to-br ${song.color} p-6 text-white`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-1">{song.recipient_name}</h3>
                    <p className="text-white/90">Age {song.age}</p>
                  </div>
                  <Heart className="w-6 h-6" />
                </div>
                <p className="text-sm text-white/90 mb-2">{song.interests}</p>
                <p className="text-sm text-white/90">{song.song_style}</p>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <button
                    onClick={() => togglePlay(song.id)}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    {activeExample === song.id ? (
                      <>
                        <Pause className="w-5 h-5" /> Pause Preview
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" /> Play Preview
                      </>
                    )}
                  </button>
                </div>

                {activeExample === song.id && (
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {song.lyrics}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={scrollToForm}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition-all inline-flex items-center gap-2"
          >
            Create Your Personalized Song
          </button>
        </div>
      </section>

      <section id="order-form" className="max-w-3xl mx-auto px-4 py-16">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold mb-4">Create Your Song</h2>
            <p className="text-gray-600">Tell us about the birthday person and we'll create their perfect song</p>
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleInputChange}
                required
                placeholder="your.email@example.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
              />
              <p className="text-xs text-gray-500 mt-1">We'll send your personalized song here</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recipient's Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="recipientName"
                  value={formData.recipientName}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Sarah"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Age <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 30"
                  min="1"
                  max="120"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Interests & Hobbies <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="interests"
                value={formData.interests}
                onChange={handleInputChange}
                required
                placeholder="e.g., gardening, cooking, traveling"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
              />
              <p className="text-xs text-gray-500 mt-1">What makes them special? What do they love?</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Special Message (Optional)
              </label>
              <textarea
                name="specialMessage"
                value={formData.specialMessage}
                onChange={handleInputChange}
                placeholder="Any special message or theme you'd like included?"
                rows="3"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Song Style <span className="text-red-500">*</span>
              </label>
              <select
                name="songStyle"
                value={formData.songStyle}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
              >
                <option value="upbeat">Upbeat & Fun</option>
                <option value="heartwarming">Heartwarming & Sweet</option>
                <option value="playful">Playful & Silly</option>
                <option value="energetic">Energetic & Modern</option>
                <option value="gentle">Gentle & Sentimental</option>
              </select>
            </div>

            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <span className="font-semibold text-gray-700">Total</span>
                <span className="text-3xl font-bold text-purple-600">$7.99</span>
              </div>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Personalized lyrics created by AI
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  High-quality audio generation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Delivered in 5-10 minutes
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Downloadable MP3 file
                </li>
              </ul>

              <button
                type="submit"
                disabled={orderStatus === 'submitting'}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl text-lg font-bold hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {orderStatus === 'submitting' ? (
                  <>
                    <Clock className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Create My Song - $7.99
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              By placing an order, you agree to our terms of service. Payment processed securely via Stripe.
            </p>
          </form>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Music className="w-6 h-6" />
                <span className="text-xl font-bold">Little Happy Tunes</span>
              </div>
              <p className="text-gray-400">
                Creating personalized birthday songs with the power of AI.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Examples</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>© 2026 Little Happy Tunes. All rights reserved. Made with AI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BirthdaySongMVP;
